/**
 * 発注一覧画面。Phase 7。
 *
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6
 * UI: イベント管理画面と統一したカード形式リスト
 *
 * 2026-10: 行を押すと商談詳細「請求・発注・発送」タブの該当発注カードへ移動。
 * TimeWaver の発注は書類 3 種 (プロフォーマ / 最終インボイス / DHL 書類) の状況を表示し、
 * 「書類が足りない発注のみ」で絞り込める。
 *
 * 2026-10-30: 「書類が足りない発注のみ」を URL (?docsMissing=1) に持たせ、商談詳細を開く時は戻り先 (発注一覧) を渡す。
 *   商談詳細の「前の画面へ」で、絞り込みとスクロール位置をそのままにここへ戻れる。
 *
 * 2026-10-30〜 入荷・検品フロー: 行の右のボタンは状態で変わる
 *   発注済 → 入荷を記録 / 入荷済 → 検品・シリアルを入力 / 検品済 → 顧客へ発送
 * 旧「出荷確定」(トラック) ボタンは廃止。状態での絞り込みを追加。
 */
import type { PurchaseOrderStatus } from '@goenhub/shared';
import { inspectionProgress, PURCHASE_ORDER_STATUS_LABEL } from '@goenhub/shared';
import {
  CheckCircle,
  ChevronRight,
  ClipboardCheck,
  Package,
  ShoppingCart,
  Truck,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDeals } from '@/hooks/useDeals';
import { useAllManufacturerQuotes } from '@/hooks/useManufacturerQuotes';
import { useProductCategories } from '@/hooks/useProductCategories';
import { useReturnToState } from '@/hooks/useReturnTo';
import { usePurchaseOrders, useUpdatePurchaseOrder } from '@/hooks/useSalesOps';
import { useUrlFlagParam } from '@/hooks/useUrlParam';
import { fmtYmd, PO_STATUS_VARIANT } from '@/lib/purchaseOrderStatus';
import { PurchaseOrderInspectionDialog } from '@/pages/deals/dialogs/PurchaseOrderInspectionDialog';
import { ShipToCustomerDialog } from '@/pages/deals/dialogs/ShipToCustomerDialog';
import { paths } from '@/routes/paths';

const STATUS_FILTERS: PurchaseOrderStatus[] = [
  'pending',
  'ordered',
  'received',
  'inspected',
  'shipped',
  'cancelled',
];

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

/** TimeWaver 発注の書類状況 (TimeWaver 以外は null)。 */
interface DocStatus {
  proforma: boolean;
  finalInvoice: boolean;
  importCount: number;
  missing: boolean;
}

function DocChip({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center h-5 px-2 rounded text-[11px] whitespace-nowrap ${
        ok
          ? 'bg-[var(--accent-green-subtle)] text-[var(--accent-green)]'
          : 'bg-[var(--accent-amber-subtle)] text-[var(--accent-amber)]'
      }`}
    >
      {children}
    </span>
  );
}

export function PurchaseOrdersListPage() {
  const navigate = useNavigate();
  const { data: pos, isLoading } = usePurchaseOrders();
  const { data: deals } = useDeals();
  const { data: categories } = useProductCategories({ includeInactive: true });
  const { data: mqs } = useAllManufacturerQuotes();
  const update = useUpdatePurchaseOrder();
  const [missingOnly, setMissingOnly] = useUrlFlagParam('docsMissing');
  const returnToState = useReturnToState('発注一覧');
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | 'all'>('all');
  const [dialog, setDialog] = useState<{ kind: 'inspection' | 'ship'; poId: string } | null>(null);

  const dealMap = useMemo(() => {
    const m = new Map<string, { title: string; primaryCategoryId: string | null }>();
    for (const d of deals ?? []) {
      m.set(d.id, { title: d.title, primaryCategoryId: d.primaryCategoryId ?? null });
    }
    return m;
  }, [deals]);

  const timeWaverCategoryIds = useMemo(
    () => new Set((categories ?? []).filter((c) => c.name === 'TimeWaver').map((c) => c.id)),
    [categories],
  );

  // `${dealId}:${invoiceId}` → プロフォーマ (メーカー見積) あり
  const proformaKeys = useMemo(() => {
    const s = new Set<string>();
    for (const mq of mqs ?? []) {
      if (mq.dealId && mq.invoiceId) s.add(`${mq.dealId}:${mq.invoiceId}`);
    }
    return s;
  }, [mqs]);

  const rows = useMemo(() => {
    return (pos ?? []).map((po) => {
      const deal = dealMap.get(po.dealId);
      const isTimeWaver =
        !!deal?.primaryCategoryId && timeWaverCategoryIds.has(deal.primaryCategoryId);
      let docs: DocStatus | null = null;
      if (isTimeWaver) {
        const proforma =
          !!po.manufacturerQuoteId ||
          (!!po.invoiceId && proformaKeys.has(`${po.dealId}:${po.invoiceId}`));
        const finalInvoice = !!po.officialOrderPdfPath;
        const importCount = (po.importDocuments ?? []).length;
        docs = {
          proforma,
          finalInvoice,
          importCount,
          missing: po.status !== 'cancelled' && (!proforma || !finalInvoice || importCount === 0),
        };
      }
      return { po, dealTitle: deal?.title ?? po.dealId, docs, isTimeWaver };
    });
  }, [pos, dealMap, timeWaverCategoryIds, proformaKeys]);

  const statusCounts = useMemo(() => {
    const c = new Map<PurchaseOrderStatus, number>();
    for (const r of rows) c.set(r.po.status, (c.get(r.po.status) ?? 0) + 1);
    return c;
  }, [rows]);

  const visibleRows = rows.filter(
    (r) =>
      (!missingOnly || r.docs?.missing) && (statusFilter === 'all' || r.po.status === statusFilter),
  );

  const dialogRow = dialog ? rows.find((r) => r.po.id === dialog.poId) : undefined;

  const markOrdered = async (dealId: string, poId: string) => {
    try {
      await update.mutateAsync({ dealId, poId, status: 'ordered' });
      toast.success(`状態を「${PURCHASE_ORDER_STATUS_LABEL.ordered}」に更新しました`);
    } catch (err) {
      toast.error(`更新失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const openPo = (dealId: string, poId: string) =>
    navigate(`${paths.dealDetail.replace(':id', dealId)}?tab=fulfillment&po=${poId}`, {
      state: returnToState,
    });

  return (
    <Screen
      title="発注管理"
      description="行を押すと、その商談の発注カードを開きます。右のボタンで 入荷・検品・顧客への発送 を進めます。"
      restoreScroll
      actions={
        <Button
          type="button"
          variant={missingOnly ? 'primary' : 'secondary'}
          size="sm"
          aria-pressed={missingOnly}
          onClick={() => setMissingOnly(!missingOnly)}
        >
          書類が足りない発注のみ
        </Button>
      }
    >
      <div
        className="flex flex-wrap items-center gap-1.5 mb-3"
        role="group"
        aria-label="状態で絞り込む"
      >
        <span className="text-xs text-[var(--text-muted)] mr-1">状態：</span>
        {(['all', ...STATUS_FILTERS] as const).map((s) => {
          const count = s === 'all' ? rows.length : (statusCounts.get(s) ?? 0);
          if (s !== 'all' && count === 0 && statusFilter !== s) return null;
          const active = statusFilter === s;
          return (
            <button
              key={s}
              type="button"
              aria-pressed={active}
              onClick={() => setStatusFilter(s)}
              className={`h-7 px-2.5 rounded-full border text-xs transition-colors ${
                active
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--text-primary)] font-semibold'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {s === 'all' ? 'すべて' : PURCHASE_ORDER_STATUS_LABEL[s]}{' '}
              <span className="tabular-nums text-[var(--text-muted)]">{count}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <Spinner />
      ) : !pos || pos.length === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          title="発注書はまだありません"
          description="請求が paid になると発注書が自動起票されます。"
        />
      ) : visibleRows.length === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          title="条件に合う発注はありません"
          description={
            missingOnly
              ? 'TimeWaver の発注は、すべて書類 3 種がそろっています。'
              : '絞り込みを変えてください。'
          }
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {visibleRows.map(({ po, dealTitle, docs, isTimeWaver }) => {
            const showProgress = po.status === 'received' || po.status === 'inspected';
            const progress = showProgress ? inspectionProgress(po, isTimeWaver) : null;
            return (
              <div
                key={po.id}
                className="flex items-center gap-2 border-b border-[var(--border)] last:border-b-0"
              >
                <button
                  type="button"
                  onClick={() => openPo(po.dealId, po.id)}
                  className="flex-1 min-w-0 flex items-center gap-4 p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
                  title="この発注の商談を開く"
                >
                  <ShoppingCart className="size-4 text-[var(--text-muted)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[13px] font-semibold truncate">
                        {po.poNumber}
                      </span>
                      <Badge variant={PO_STATUS_VARIANT[po.status]}>
                        {PURCHASE_ORDER_STATUS_LABEL[po.status]}
                      </Badge>
                    </div>
                    <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                      <span>{dealTitle}</span>
                      <span>仕入先: {po.supplierName ?? '-'}</span>
                      <span className="tabular-nums">¥{po.totalCostPrice.toLocaleString()}</span>
                      <span>発注: {fmtDate(po.orderedAt)}</span>
                      {po.inspection?.arrivedAt && (
                        <span>入荷: {fmtYmd(po.inspection.arrivedAt)}</span>
                      )}
                      {po.status === 'shipped' && po.shippedAt && (
                        <span>発送: {fmtDate(po.shippedAt)}</span>
                      )}
                    </div>
                    {(docs || progress) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {docs && (
                          <>
                            <DocChip ok={docs.proforma}>
                              プロフォーマ {docs.proforma ? '✓' : '－'}
                            </DocChip>
                            <DocChip ok={docs.finalInvoice}>
                              最終インボイス {docs.finalInvoice ? '✓' : '－'}
                            </DocChip>
                            <DocChip ok={docs.importCount > 0}>DHL {docs.importCount}件</DocChip>
                          </>
                        )}
                        {progress && (
                          <DocChip ok={progress.ready}>
                            {progress.inspectedChecked ? '検品済' : '検品 未'} ·{' '}
                            {isTimeWaver
                              ? `そろった機器 ${progress.completeUnits}/${progress.totalUnits}`
                              : `シリアル ${progress.filledSerials}/${progress.totalUnits}`}
                          </DocChip>
                        )}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="size-4 text-[var(--text-muted)] flex-shrink-0" />
                </button>
                {po.status === 'pending' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mr-3"
                    onClick={() => markOrdered(po.dealId, po.id)}
                    title="発注確定"
                    aria-label="発注確定"
                    disabled={update.isPending}
                  >
                    <CheckCircle />
                  </Button>
                )}
                {po.status === 'ordered' && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mr-3 shrink-0"
                    onClick={() => setDialog({ kind: 'inspection', poId: po.id })}
                  >
                    <Package />
                    入荷を記録
                  </Button>
                )}
                {po.status === 'received' && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mr-3 shrink-0"
                    onClick={() => setDialog({ kind: 'inspection', poId: po.id })}
                  >
                    <ClipboardCheck />
                    検品・シリアルを入力
                  </Button>
                )}
                {po.status === 'inspected' && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="mr-3 shrink-0"
                    onClick={() => setDialog({ kind: 'ship', poId: po.id })}
                  >
                    <Truck />
                    顧客へ発送
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {dialogRow && (
        <>
          <PurchaseOrderInspectionDialog
            open={dialog?.kind === 'inspection'}
            onOpenChange={(o) => !o && setDialog(null)}
            dealId={dialogRow.po.dealId}
            po={dialogRow.po}
            isTimeWaver={dialogRow.isTimeWaver}
            onProceedToShip={() => setDialog({ kind: 'ship', poId: dialogRow.po.id })}
          />
          <ShipToCustomerDialog
            open={dialog?.kind === 'ship'}
            onOpenChange={(o) => !o && setDialog(null)}
            dealId={dialogRow.po.dealId}
            po={dialogRow.po}
            isTimeWaver={dialogRow.isTimeWaver}
          />
        </>
      )}
    </Screen>
  );
}
